/**
 * Главный класс игры: центральный барабан (один спин), статичные круги (заполняются по цвету).
 * Ставка списывается после спина. Спин — кнопка или Пробел.
 */

import * as PIXI from 'pixi.js';
import type { GameState, RingColor } from '@/types/gameState';
import { INITIAL_STATE } from '@/types/gameState';
import { VerticalReel } from '@/rings/VerticalReel';
import { StaticRings } from '@/rings/StaticRings';
import { BET_CONFIG } from '@/config/betConfig';
import { computePayout } from '@/utils/math';
import { COLORS } from '@/config/colors';
import { DRUM_SEGMENTS, RING_MULTIPLIERS_RAW, LAST_SEGMENT_BONUS, BONUS_GAME_MULTIPLIERS, type DrumSegmentConfig } from '@/config/drumConfig';

export type GameEventType =
  | 'state_changed'
  | 'balance_changed'
  | 'bet_changed'
  | 'multiplier_changed'
  | 'payout_changed'
  | 'spin_start'
  | 'spin_stop'
  | 'reel_stop'
  | 'round_started'
  | 'round_ended'
  | 'segment_hit'
  | 'fruit_cut'
  | 'juice_splash'
  | 'sector_fill'
  | 'sector_drain'
  | 'bonus_won'
  | 'instant_bonus_cashout';

export interface GameEvent {
  type: GameEventType;
  state?: GameState;
  segment?: DrumSegmentConfig;
  spinId?: number;
  /** Множитель бонусной игры (x100, x200, …) при type === 'bonus_won' */
  bonusMultiplier?: number;
  /** Сумма мгновенного кэшаута за последний сектор (green/blue) */
  bonusCashout?: number;
  bonusCashoutColor?: RingColor;
  bonusCashoutIndex?: number;
  ringColor?: RingColor;
  ringIndex?: number;
}

type GameEventListener = (event: GameEvent) => void;

const SPIN_DURATION_NORMAL_MS = 1700;
const SPIN_DURATION_TURBO_MS = 900;

export class Game {
  private state: GameState;
  private reel: VerticalReel;
  private staticRings: StaticRings;
  private listeners: Partial<Record<GameEventType, Set<GameEventListener>>> = {};
  private gameContainer: PIXI.Container;
  private spinId = 0;
  private spinStartTime = 0;
  private pendingResultSegment: DrumSegmentConfig | null = null;
  private pendingResultIndex = 0;
  private stopRequested = false;
  private currentSpinDurationMs = SPIN_DURATION_NORMAL_MS;
  private pendingRedBonusReset = false;
  /** Не переходить в IDLE, пока кольца не доиграют анимацию заливки/слива (последняя секция). */
  private waitingForRingAnimation = false;
  /** Instant bonus (зелёный/синий последний): слив стартует через 260ms; не переходить в IDLE до этого. */
  private waitingForInstantBonusDrainStart = false;
  /** Ждём короткую задержку перед заливкой, чтобы успел проиграться меч. */
  private waitingForFillDelay = false;

  constructor(gameContainer: PIXI.Container) {
    this.gameContainer = gameContainer;
    this.state = { ...INITIAL_STATE };
    this.staticRings = new StaticRings();
    this.reel = new VerticalReel();
    // Порядок важен: сначала статические кольца (фон), потом барабан (поверх)
    // Но подсветка должна быть видна, поэтому она добавляется внутри StaticRings после всех элементов
    this.gameContainer.addChild(this.staticRings);
    this.gameContainer.addChild(this.reel);
    this.staticRings.setFilledSectors(this.state.filledSectors);
    this.staticRings.setOnFillStart((color, index) => {
      this.emit({ type: 'sector_fill', ringColor: color, ringIndex: index });
    });
    this.staticRings.setOnDrainStart((color, index) => {
      this.emit({ type: 'sector_drain', ringColor: color, ringIndex: index });
    });
    this.initializeReel();
  }

  /** Показать описание бонусной игры (иконка «i» у BONUS) */
  setBonusInfoCallback(callback: () => void): void {
    this.staticRings.setBonusInfoCallback(callback);
  }

  private initializeReel(): void {
    if (this.state.lastResult) {
      const lastSeg = this.state.lastResult;
      const index = DRUM_SEGMENTS.findIndex(
        (seg) => seg.type === lastSeg.type && seg.value === lastSeg.value
      );
      if (index >= 0) {
        this.reel.setInitialElement(index);
        return;
      }
    }
    this.reel.setInitialElement(0);
  }


  getState(): GameState {
    return { ...this.state };
  }

  getBalance(): number {
    return this.state.balance;
  }

  getBet(): number {
    return this.state.bet;
  }

  getMultiplier(): number {
    return this.state.multiplier;
  }

  getPayout(): number {
    return this.state.payout;
  }

  setBet(bet: number): void {
    const clamped = Math.max(BET_CONFIG.min, Math.min(BET_CONFIG.max, bet));
    if (this.state.type !== 'IDLE') return;
    if (this.state.bet === clamped) return; // Не эмитим, если значение не изменилось — иначе цикл с BetControl
    this.state.bet = clamped;
    this.emit({ type: 'bet_changed' });
  }

  increaseBet(): void {
    this.setBet(this.state.bet + BET_CONFIG.step);
  }

  decreaseBet(): void {
    this.setBet(this.state.bet - BET_CONFIG.step);
  }

  canSpin(): boolean {
    return (
      this.state.type === 'IDLE' &&
      this.state.balance >= this.state.bet &&
      this.state.bet >= BET_CONFIG.min &&
      !this.staticRings.isAnimating()
    );
  }

  startSpin(turbo = false): void {
    if (!this.canSpin()) return;
    // Списываем ставку сразу при начале спина
    this.state.balance -= this.state.bet;
    this.emit({ type: 'balance_changed' });
    
    this.state.type = 'SPINNING';
    this.pendingResultSegment = null;
    this.pendingResultIndex = 0;
    this.stopRequested = false;
    this.spinStartTime = performance.now();
    this.spinId += 1;
    this.emit({ type: 'state_changed' });
    this.emit({ type: 'spin_start', spinId: this.spinId });
    this.emit({ type: 'round_started' });
    this.currentSpinDurationMs = turbo ? SPIN_DURATION_TURBO_MS : SPIN_DURATION_NORMAL_MS;
    this.reel.startSpin(turbo);
  }

  /** Мгновенно остановить спин: результат выбирается сейчас, барабан красиво доезжает до него. */
  requestStopSpin(): void {
    if (this.state.type === 'SPINNING' && this.pendingResultSegment === null) {
      // Немедленно останавливаемся, не ждем следующего кадра
      this.stopRequested = false; // Сбрасываем флаг, так как обрабатываем сразу
      const segIdx = this.reel.getRandomElementIndex();
      this.pendingResultSegment = this.reel.stopAtElement(segIdx, true);
      this.pendingResultIndex = segIdx;
      this.emit({ type: 'spin_stop' });
    }
  }

  update(delta: number): void {
    this.staticRings.update(delta);
    // Переход в IDLE после завершения анимации колец (заливка/слив)
    if (
      this.waitingForRingAnimation &&
      !this.waitingForFillDelay &&
      !this.waitingForInstantBonusDrainStart &&
      !this.staticRings.isAnimating() &&
      (this.state.type === 'RESOLVING' || this.state.type === 'SKULL')
    ) {
      this.waitingForRingAnimation = false;
      this.state.type = 'IDLE';
      this.emit({ type: 'state_changed' });
    }
    if (this.state.type === 'SPINNING') {
      if (this.stopRequested) {
        if (this.pendingResultSegment === null) {
          this.stopRequested = false;
          const segIdx = this.reel.getRandomElementIndex();
          this.pendingResultSegment = this.reel.stopAtElement(segIdx);
          this.pendingResultIndex = segIdx;
          this.emit({ type: 'spin_stop' });
        }
      } else {
        if (this.pendingResultSegment === null) {
          const elapsed = performance.now() - this.spinStartTime;
          if (elapsed >= this.currentSpinDurationMs) {
            const segIdx = this.reel.getRandomElementIndex();
            this.pendingResultSegment = this.reel.stopAtElement(segIdx);
            this.pendingResultIndex = segIdx;
            this.emit({ type: 'spin_stop' });
          }
        }
      }
    }
    // Барабан обновляем каждый кадр в одном месте (спин + анимация разреза после остановки)
    const stillSpinning = this.reel.update(delta);
    if (this.state.type === 'SPINNING' && !stillSpinning && this.pendingResultSegment !== null) {
      const segment = this.pendingResultSegment;
      const segIdx = this.pendingResultIndex;
      this.pendingResultSegment = null;
      this.emit({ type: 'reel_stop' });
      this.resolveResult(segment, segIdx);
    }
  }

  private resolveResult(segment: DrumSegmentConfig, segIdx: number): void {
    this.state.lastResult = {
      type: segment.type,
      value: segment.value,
    };
    this.state.type = 'RESOLVING';
    this.emit({ type: 'state_changed' });

    setTimeout(() => {
      let skipFinalRingUpdate = false;
      if (segment.type === 'skull') {
        const prev = this.state.filledSectors;
        this.state.filledSectors = {
          red: prev.red.length > 0 ? prev.red.slice(0, -1) : [],
          green: prev.green.length > 0 ? prev.green.slice(0, -1) : [],
          blue: prev.blue.length > 0 ? prev.blue.slice(0, -1) : [],
        };
        this.recalcMultiplierAndPayout();
        this.reel.highlightElement(segIdx, COLORS.red);
        this.state.type = 'SKULL';
        this.waitingForRingAnimation = true;
        this.emit({ type: 'segment_hit', segment });
        this.emit({ type: 'multiplier_changed' });
        this.emit({ type: 'payout_changed' });
      } else if (segment.type === 'gray') {
        this.reel.highlightElement(segIdx, COLORS.gray);
        this.emit({ type: 'segment_hit', segment });
      } else if (
        (segment.type === 'red' || segment.type === 'green' || segment.type === 'blue') &&
        segment.value != null
      ) {
        const cutStarted = this.reel.playCutAnimationIfFruit();
        if (cutStarted) {
          this.emit({ type: 'fruit_cut', segment, spinId: this.spinId });
          this.emit({ type: 'juice_splash', segment });
        }
        const color = segment.type as RingColor;
        const multipliersRaw = RING_MULTIPLIERS_RAW[color];
        const currentFilled = this.state.filledSectors[color];
        const nextIndex = currentFilled.length;
        if (nextIndex < multipliersRaw.length) {
          const nextMultiplier = multipliersRaw[nextIndex];
          const isLastSectionOfRing = nextIndex === multipliersRaw.length - 1;

          this.waitingForRingAnimation = true;
          this.state.filledSectors[color] = [...currentFilled, nextMultiplier];
          this.recalcMultiplierAndPayout();
          this.reel.highlightElement(segIdx, COLORS.gold);
          this.emit({ type: 'segment_hit', segment });

          if (isLastSectionOfRing) {
            const lastBonus = LAST_SEGMENT_BONUS[color];
            if (lastBonus === 'BONUS') {
              // Бонусная игра: все огненные (красные) секции заполнены
              // Случайный множитель x100, x200, x300, x400, x500 — равный шанс
              const idx = Math.floor(Math.random() * BONUS_GAME_MULTIPLIERS.length);
              const bonusMultiplier = BONUS_GAME_MULTIPLIERS[idx];
              this.state.balance += this.state.bet * bonusMultiplier;
              this.emit({ type: 'balance_changed' });
              // Сбросим красные секции после анимации бонуса
              this.pendingRedBonusReset = true;
              this.emit({ type: 'bonus_won', bonusMultiplier });
            } else {
              // Синяя (+x7) или зелёная (+x20): авто-начисление и снятие окрашивания
              const bonusMultiplier = lastBonus as number;
              const bonusCashout = this.state.bet * bonusMultiplier;
              this.state.balance += bonusCashout;
              this.emit({ type: 'balance_changed' });
              this.emit({
                type: 'instant_bonus_cashout',
                bonusCashout,
                bonusCashoutColor: color,
                bonusCashoutIndex: nextIndex,
              });
              this.waitingForRingAnimation = true;
              // Слив секции стартует через 260ms; не переходить в IDLE до старта слива (заливка может закончиться раньше)
              this.waitingForInstantBonusDrainStart = true;
              // Показываем заполнение, затем откат в исходное состояние (анимация слива доиграет до конца)
              this.staticRings.setFilledSectors({ ...this.state.filledSectors });
              skipFinalRingUpdate = true;
              setTimeout(() => {
                this.waitingForInstantBonusDrainStart = false;
                this.state.filledSectors[color] = currentFilled;
                this.recalcMultiplierAndPayout();
                this.staticRings.setFilledSectors(this.state.filledSectors);
                this.emit({ type: 'multiplier_changed' });
                this.emit({ type: 'payout_changed' });
              }, 260);
            }
          }
          this.emit({ type: 'multiplier_changed' });
          this.emit({ type: 'payout_changed' });
        }
      }

      if (!skipFinalRingUpdate) {
        const fillDelayMs = 640;
        this.waitingForFillDelay = true;
        setTimeout(() => {
          this.staticRings.setFilledSectors(this.state.filledSectors);
          this.waitingForFillDelay = false;
        }, fillDelayMs);
      }

      // Ставка уже списана при начале спина

      if (!this.waitingForRingAnimation) {
        this.state.type = 'IDLE';
        this.emit({ type: 'state_changed' });
      }
    }, 400);
  }

  private recalcMultiplierAndPayout(): void {
    const { red, green, blue } = this.state.filledSectors;
    const lastValue = (arr: number[]) =>
      arr.length === 0 ? 0 : arr[arr.length - 1];
    this.state.multiplier =
      lastValue(red) + lastValue(green) + lastValue(blue);
    this.state.payout = computePayout(this.state.bet, this.state.multiplier);
  }

  cashout(): void {
    if (this.state.payout <= 0) return;
    this.state.balance += this.state.payout;
    this.state.type = 'CASHOUT';
    this.emit({ type: 'balance_changed' });
    this.emit({ type: 'state_changed' });
    setTimeout(() => this.endRound(), 800);
  }

  private endRound(): void {
    this.state.type = 'IDLE';
    this.state.multiplier = 1;
    this.state.payout = 0;
    this.state.filledSectors = { red: [], green: [], blue: [] };
    this.staticRings.setFilledSectors(this.state.filledSectors);
    this.emit({ type: 'round_ended' });
    this.emit({ type: 'state_changed' });
  }

  finalizeRedBonusReset(): void {
    if (!this.pendingRedBonusReset) return;
    this.pendingRedBonusReset = false;
    this.state.filledSectors.red = [];
    this.recalcMultiplierAndPayout();
    this.staticRings.setFilledSectors(this.state.filledSectors);
    this.emit({ type: 'multiplier_changed' });
    this.emit({ type: 'payout_changed' });
  }

  on(_event: GameEventType, listener: GameEventListener): void {
    const event = _event;
    if (!this.listeners[event]) this.listeners[event] = new Set<GameEventListener>();
    this.listeners[event]!.add(listener);
  }

  off(_event: GameEventType, listener: GameEventListener): void {
    const event = _event;
    this.listeners[event]?.delete(listener);
  }

  private emit(event: GameEvent): void {
    event.state = this.getState();
    if (event.spinId == null) event.spinId = this.spinId;
    const listeners = this.listeners[event.type];
    if (!listeners) return;
    for (const listener of listeners) {
      listener(event);
    }
  }
}
